import { AfterViewInit, Component, DestroyRef, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { DatastoreProperties } from "../model/datastore-properties.model";
import { PropertiesService } from "../services/properties.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { BehaviorSubject, of, switchMap } from "rxjs";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { setLoader } from "src/app/utils/set-loaders";
import moment from "moment";
import { Property } from "src/app/shared/enums/DatastoreProperty";

export const markAsRequired = (properties: string[]): ValidatorFn => {
  return (control: FormGroup): ValidationErrors | null => {
    const propertyControl = control.get("property");
    const properyValueControl = control.get("propertyValue");

    if (properties.includes(propertyControl.value) && !properyValueControl.value) {
      properyValueControl.setErrors({ required: true });
      return { required: true };
    } else {
      properyValueControl.setErrors(null);
      return null;
    }
  };
};

@Component({
  selector: "app-datastore-properties",
  templateUrl: "./datastore-properties.component.html",
  styleUrl: "./datastore-properties.component.scss",
})
export class DatastorePropertiesComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  @ViewChild("dialogAddTpl") dialogAddTpl: TemplateRef<any>;

  private _propertiesService = inject(PropertiesService);

  private _dialog = inject(MatDialog);

  private _fb = inject(FormBuilder);

  private _destroyRef = inject(DestroyRef);

  private _reloader = new BehaviorSubject(false);

  private _dialogRef: MatDialogRef<any>;

  loaders$ = new BehaviorSubject({});

  displayedColumns = ["moduleName", "displayName", "propertyValue", "actions"];

  datasource = new MatTableDataSource<DatastoreProperties>([]);

  timezones = moment.tz.names();

  selectOptions = [];

  filteredOptions = [];

  allowAdd = false;

  datastorePropsForm = this._fb.group(
    {
      moduleId: [""],
      moduleName: this._fb.control({ value: "", disabled: true }),
      type: this._fb.control({ value: "", disabled: true }),
      property: this._fb.control({ value: "", disabled: true }),
      displayName: this._fb.control({ value: "", disabled: true }),
      propertyValue: this._fb.control({ value: "", disabled: false }),
      description: this._fb.control({ value: "", disabled: true }),
    },
    {
      validators: [
        markAsRequired([Property.CAMUNDA, Property.ORG_ID, Property.AMDT_NOTICE_PERIOD, Property.SUP_NOTICE_PERIOD]),
      ],
    },
  );

  get fc() {
    return this.datastorePropsForm.controls;
  }

  constructor() {}

  ngOnInit() {
    this.getDatastoreProperties();
  }

  ngAfterViewInit(): void {
    this.datasource.paginator = this.paginator;
    this.datasource.sort = this.sort;
  }

  getDatastoreProperties(): void {
    this._reloader
      .pipe(
        switchMap(() => this._propertiesService.getDatastoreProperties()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((datastoreProperties) => {
        this.datasource.data = datastoreProperties;
      });
  }

  openDialogAddEdit(actions: "add" | "edit", datastoreProps?: DatastoreProperties): void {
    if (actions === "edit") {
      this.datastorePropsForm.patchValue(datastoreProps);
      const property = this.datastorePropsForm.get("property").value.toLowerCase();
      const propertiesOptions = this._getPropertiesOptions(this.datasource);
      const optionsMapper = {
        ...propertiesOptions,
        [Property.TIMEZONE]: this.timezones,
      };
      this.selectOptions = optionsMapper[property] ?? [];
      this.filteredOptions = this.selectOptions;
    }

    this._dialogRef = this._dialog.open(this.dialogAddTpl, {
      width: "500px",
      data: { actions },
    });

    this._dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.datastorePropsForm.reset();
      });
  }

  private _getPropertiesOptions(datasource: MatTableDataSource<DatastoreProperties>) {
    return datasource.data.reduce((acc, props) => {
      if (props.type === "LIST" && !acc[props.property]) acc[props.property] = props.propertyValue;
      return acc;
    }, {});
  }

  addEditDatastoreProperty(actions: "add" | "edit"): void {
    const { moduleId, ...addPayload } = this.datastorePropsForm.value;
    const editPayload = this.datastorePropsForm.getRawValue() as DatastoreProperties;
    const mutations = {
      add: this._propertiesService.addDatastoreProperty(addPayload as DatastoreProperties),
      edit: this._propertiesService.editDatastoreProperty(editPayload),
    };

    mutations[actions]
      .pipe(setLoader(this.loaders$, "dsPropMutation"), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._dialogRef.close();
        this._reloader.next(true);
      });
  }

  filterOptions(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value;
    of(value)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((searchValue) => {
        if (!this.selectOptions.length) return;
        const normalized = (searchValue || "").trim().toLowerCase();
        const selectedValues = this.fc.propertyValue.value || [];

        this.filteredOptions = this.selectOptions.filter((option: string) => {
          const matchSearch = option.toLowerCase().includes(normalized);
          const isSelected = selectedValues.includes(option);
          return matchSearch || isSelected;
        });
      });
  }
}
